import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { FriendMatch } from '../types/social';

interface Args {
  myDuplicateCodes: string[];
  friendIds: string[];
  friendNamesById: Record<string, string>;
}

interface Result {
  matches: FriendMatch[];
  loading: boolean;
  refresh: () => Promise<void>;
}

/**
 * Lê wishlist de cada amigo cruzando com minhas figurinhas com count >= 2.
 * Retorna lista ordenada (priority asc), uma linha por (friend, sticker_code).
 */
export function useFriendMatches({
  myDuplicateCodes,
  friendIds,
  friendNamesById,
}: Args): Result {
  const [matches, setMatches] = useState<FriendMatch[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (myDuplicateCodes.length === 0 || friendIds.length === 0) {
      setMatches([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data, error } = await supabase
      .from('wishlist')
      .select('user_id, sticker_code, priority')
      .in('user_id', friendIds)
      .in('sticker_code', myDuplicateCodes)
      .order('priority', { ascending: true });

    if (error) {
      console.error('useFriendMatches', error);
      setMatches([]);
      setLoading(false);
      return;
    }

    const result: FriendMatch[] = (data ?? []).map(
      (r: { user_id: string; sticker_code: string; priority: number }) => ({
        friend_id: r.user_id,
        friend_name: friendNamesById[r.user_id] ?? 'Amigo',
        sticker_code: r.sticker_code,
        priority: r.priority,
      })
    );
    setMatches(result);
    setLoading(false);
  }, [myDuplicateCodes, friendIds, friendNamesById]);

  useEffect(() => {
    load();
  }, [load]);

  return { matches, loading, refresh: load };
}
